import { SaveIntegrationView } from '../view/SaveIntegrationView';
import { templateSaveIntegration } from '../view/template';
import { BaseWebComponentController, IListenerConfig } from '../../../framework/BaseWebComponentController';
import { SaveIntegrationWebComponent } from '..';
import { SaveIntegrationEvents } from '../types';
import { showToast } from '../../../accounts/helpers/showToast';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { translateFn } from '../../../utils/translator.helper';
import { IntegrationsMarketplaceAppContainerNirvanaDataProvider } from '../../integrationsMarketplaceAppContainer/dataProvider/IntegrationsMarketplaceAppContainerNirvanaDataProvider';
import { IntegrationActionContext, IntegrationTypes } from '../../integrationsMarketplaceGridInstalled/types';
import { Subscription } from 'rxjs';
import { SaveIntegrationRepository } from '../repository/SaveIntegrationRepository';
import { IntegrationControllerFactory } from '../../integrations/IntegrationControllerFactory';
import { IntegrationData } from '../../integrations/types';

export class SaveIntegrationController extends BaseWebComponentController {
  public component: SaveIntegrationWebComponent;
  public view: SaveIntegrationView;
  public viewConstructor: typeof SaveIntegrationView;
  public dataRepositoryConstructor: typeof SaveIntegrationRepository;
  public dataRepository: SaveIntegrationRepository;
  private isFormValid: boolean;
  private formSubscription: Subscription;

  constructor(component: SaveIntegrationWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: SaveIntegrationView,
      viewTemplate: templateSaveIntegration,
      dataRepositoryConstructor: SaveIntegrationRepository,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$createButton,
        event: 'click',
        callback: event => this.submitCreateIntegrationForm(event),
      },
      {
        element: this.view.dom.$cancelButton,
        event: 'click',
        callback: this.closeSaveIntegration,
      },
      {
        element: this.view.dom.$doneButton,
        event: 'click',
        callback: this.closeDoneIntegration,
      },
      {
        element: this.view.dom.$wrapper,
        event: SaveIntegrationEvents.InitSaveIntegrationForm,
        callback: this.setFormValidation,
      },
    ];
  }

  async getDataForView(): Promise<{
    context: IntegrationActionContext | null;
    data: IntegrationData<unknown> | null;
    type: number | null;
  }> {
    let context, data;
    try {
      context = this.component.actionContext;
    } catch (e) {
      context = null;
    }

    if (context === IntegrationActionContext.Edit) {
      data = await this.dataRepository.getIntegrationById(this.component.integrationId);
    } else {
      data = null;
    }

    return {
      context: context,
      data: data,
      type: this.component.type,
    };
  }

  private async submitCreateIntegrationForm(event: Event): Promise<void> {
    const type = this.component.type;

    if (!this.isFormValid) {
      // call setFormValidation to show error messages
      this.setFormValidation();
      return;
    }

    try {
      await this.saveIntegration(type);

      showToast(ToastSeverity.SUCCESS, translateFn('Integration successfully saved.'));
      this.component.dispatchEvent(
        new CustomEvent(SaveIntegrationEvents.ReloadGrid, {
          bubbles: true,
          composed: true,
        })
      );
    } catch (error) {
      console.log(error);
      showToast(ToastSeverity.ERROR, error.err.message);
    }
  }

  private async saveIntegration(type): Promise<any> {
    let context: IntegrationActionContext | null = null;
    try {
      context = this.component.actionContext;
    } catch (e) {
      console.error('Failed to get action context:', e);
    }

    const { $createButton } = this.view.dom;

    $createButton.setLoadingState(true);

    try {
      const controller = IntegrationControllerFactory.createController(type, this.component);
      const record = controller.createIntegrationRecord(this.view.dom.$integrationWebComponent);

      if (record === null) {
        // createIntegrationRecord returned null, no need to continue saving integration.
        return;
      }

      if (context === IntegrationActionContext.Edit) {
        record['_id'] = this.component.integrationId;
      }

      const records = [record];
      const dataProvider = new IntegrationsMarketplaceAppContainerNirvanaDataProvider();

      return context === IntegrationActionContext.Edit
        ? await dataProvider.updateRecords(records)
        : await dataProvider.createRecords(records);
    } catch (error) {
      console.error('Error while saving integration:', error);
      throw error; // Re-throw the error for further handling
    } finally {
      $createButton.setLoadingState(false);
    }
  }

  private closeSaveIntegration(): void {
    this.component.dispatchEvent(
      new CustomEvent(SaveIntegrationEvents.Cancel, {
        bubbles: true,
      })
    );
  }

  private closeDoneIntegration() {
    console.log('closeDoneIntegration');
    this.component.dispatchEvent(
      new CustomEvent(SaveIntegrationEvents.ReloadGrid, {
        bubbles: true,
        composed: true,
      })
    );
  }

  private setFormValidation(): void {
    console.log('setFormValidation', this.view.dom.$integrationWebComponent);

    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }

    if (this.view.dom.$integrationWebComponent?.valid$) {
      this.formSubscription = this.view.dom.$integrationWebComponent?.valid$.subscribe({
        next: (isFormValid: boolean) => {
          this.isFormValid = isFormValid;
          console.log('Form Validity Status:', this.isFormValid);
          this.view.dom.$createButton.disabled = !this.isFormValid;
        },
        error: err => {
          console.error('An error occurred while validating the form:', err);
        },
        complete: () => {
          console.log('Form validation completed.');
        },
      });
    }
  }
}
